
import { createContext, useContext, useState, useCallback, useEffect, useMemo } from "react";
import FormHelper from "../../helpers/FormHelper";
import IUser from "../../interfaces/IUser";
import AuthService from "../../services/AuthService";
import { useNotificationContext } from "../NotificationContext";
import { useLanguageContext } from "../LanguageContext";
import { authTokenStorage, enterpriseIdStorage, sessionIdStorage } from "../../helpers/StorageHelper";
import IEnterprise from "../../interfaces/IEnterprise";
import DateTimeHelper from "../../helpers/DateTimeHelper";
import { useEnvironmentContext } from "../EnvironmentContext";
import IEnvironment from "../../interfaces/IEnvironment";


interface IUserContext {
    user: IUser | undefined;
	userPermissions: string[];
	handleChangeUser: (user: IUser) => void;

    enterprise: IEnterprise | undefined;
	handleChangeEnterprise: (enterprise: IEnterprise) => void;

	handleLogin: (formData: FormData) => void;
	handleLogout: () => void;
}


const UserContext = createContext<IUserContext | undefined>(undefined);

const UserContextProvider = (props: React.PropsWithChildren) => {
    const { showLoading, hideLoading, showToast, dismissToast } = useNotificationContext();
    const { handleChangeLanguage, translate } = useLanguageContext();
    const { handleChangeEnvironment } = useEnvironmentContext();

    const [user, setUser] = useState<IUser | undefined>(undefined);
    const [userPermissions, setUserPermissions] = useState<string[]>([]);
    const [enterprise, setEnterprise] = useState<IEnterprise | undefined>(undefined);


    useEffect(() => {
        if (!sessionIdStorage.exists()) {
            sessionIdStorage.create();
        }

        handleLoginWithStoredAuthToken();
        // eslint-disable-next-line
    }, []);


    useEffect(() => {
        if (user) {
            handleChangeLanguage(user.language);

            if (user.enterprises && user.enterprises.length === 1) {
                handleChangeEnterprise(user.enterprises[0] as IEnterprise);
            } else {
                const storedEnterpriseId = enterpriseIdStorage.get();
                if (storedEnterpriseId) {
                    const enterprise = user.enterprises.find(e => Number(e.id) === storedEnterpriseId && !e.expired);
                    if (enterprise) {
                        handleChangeEnterprise(enterprise as IEnterprise);
                    }
                }
            }
        }

        // eslint-disable-next-line
    }, [user]);


    useEffect(() => {
        if (enterprise) {
            const diff = DateTimeHelper.Diff(enterprise.expiresAt, DateTimeHelper.Now());
            
            if (diff < 5) {
                dismissToast();

                let sulfix = diff === 0 ? 'today' : (diff === 1 ? 'tomorrow' : 'days');
                sulfix = translate(sulfix).toLowerCase();

                if (diff > 1) {
                    sulfix = `${diff} ${sulfix}`;
                }

                const toastMessage = `${translate('_userContextHandleLoginEnterprisesExpiresWarning')} (${sulfix})`;
                showToast("warning", toastMessage, 10_000);
            }
        }

        // eslint-disable-next-line
    }, [enterprise]);


    const handleChangeEnterprise = useCallback((enterprise: IEnterprise) => {
        setEnterprise(enterprise);

        const permissions = (user?.enterprises.find(e => e.id === enterprise.id)?.permissions.map(p => p.alias) ?? []).reduce((prev: string[], curr: string) => {
            const rest = [...prev, curr];

            curr.split('.').slice(0, -1).reduce((prev1: string, curr1: string) => {
                const rest1 = prev1.length > 0 ? `${prev1}.${curr1}` : curr1;

                if (!rest.includes(rest1)) {
                    rest.push(rest1);
                }

                return rest1
            }, '');

            return rest;
        }, []);
        setUserPermissions(permissions);

        enterpriseIdStorage.set(enterprise.id);
        // eslint-disable-next-line
    }, [user]);

    
    const handleLogin = useCallback((formData: FormData) => {
        AuthService.Login(formData)
            .then(resp => {
                const user = resp.data.user as IUser;

                if (user.enterprises.filter(e => !e.expired).length === 0) {
                    showToast("warning", translate('_userContextHandleLoginEnterprisesExpiredError'));
                    handleLogout(false);
                } else {
                    setUser(user);
                    handleChangeEnvironment(resp.data.environment as IEnvironment);
                }
            });

        // eslint-disable-next-line
    }, []);


    const handleLogout = useCallback((closeAllToasts: boolean) => {
        const formData = FormHelper.Create(undefined);
        AuthService.Logout(formData)
            .then(() => {
                authTokenStorage.remove();
                enterpriseIdStorage.remove();
                
                setUser(undefined);
                setUserPermissions([]);
                setEnterprise(undefined);

                if (closeAllToasts) {
                    dismissToast();
                }
            });
        
        // eslint-disable-next-line
    }, []);


    const handleLoginWithStoredAuthToken = useCallback(() => {
        const authTokenStored = authTokenStorage.get();
        
        if (authTokenStored) {
            showLoading();

            const formData = FormHelper.Create(undefined);
            AuthService.LoginWithStoredAuthToken(formData)
                .then(resp => {
                    const user = resp.data.user as IUser;
                    
                    if (user.enterprises.filter(e => !e.expired).length === 0) {
                        showToast("warning", translate('_userContextHandleLoginEnterprisesExpiredError'));
                        handleLogout(false);
                    } else {
                        setUser(user);
                        handleChangeEnvironment(resp.data.environment as IEnvironment);
                    }
                })
                .catch(() => {
                    handleLogout(true);
                })
                .finally(() => {
                    hideLoading();
                });
        } else {
            handleLogout(true);
            hideLoading();
        }

        // eslint-disable-next-line
    }, []);


    const data: IUserContext = useMemo(() => {
        return {
            user,
            userPermissions,
            handleChangeUser: setUser,
            enterprise,
            handleChangeEnterprise,
            handleLogin,
            handleLogout: () => handleLogout(true)
        };

        // eslint-disable-next-line
    }, [user, userPermissions, enterprise]);
    

    return (
        <UserContext.Provider value={data}>
            {props.children}
        </UserContext.Provider>
    );
}


const useUserContext = () => {
    const context = useContext(UserContext);

    if (context === undefined) {
        throw new Error('useUserContext must be used within a UserContextProvider');
    }

    return context;
}

export {
    useUserContext
}

export default UserContextProvider;